// @flow

import * as React from 'react';

import type { FetchInfosAction, Currency } from '@gandi/billing';
import { AddressField } from '@gandi/react-places';
import { selectOneOrganization } from '@gandi/redux-organization';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { Organization } from '@gandi/redux-sharing';
import type { ActionReturn } from '@gandi/resource/redux';
import type { TranslatorProps } from '@gandi/translate';

import { SubmissionError } from 'gandi.react-form';

import { selectShopCurrent } from 'src/modules/organization/selectors';
import type {
  WithRouterProps,
  PreFetchArgs,
  PreFetchReturn,
  FullState,
} from 'src/types';

import AccountForm from '../../components/Form';

export type StateProps = {
  bu?: number,
  orgId: string,
  org: ?Organization,
  currencies: Array<Currency>,
  currency: ?Currency,
  prepaidAmount?: number,
  hasOwnerOnly: boolean,
  fetchingInfo: boolean,
  loadedInfo: boolean,
  fetchingOrg: boolean,
  loadedOrg: boolean,
  myself: boolean,
};

export type DispatchProps = {
  fetchOneOrganization: (orgId: string) => Promise<any>,
  fetchInfo: FetchInfosAction<ActionReturn>,
  patchOrganizationAndPrepaid: (
    orgId: string,
    patchData: Partial<Organization>,
    currency: string | null,
    dryrun: boolean,
  ) => Promise<any>,
  setPatchContactsFlag: (flag: boolean) => void,
  fetchOneOrganization: (orgId: string) => Promise<any>,
  destroyForm: () => mixed,
};

type Params = {
  cartId: string,
  type?: 'individual' | 'company' | 'association' | 'publicbody',
  orgId: string,
};

export type InjectedProps = {
  translator: TranslatorProps,
  ...WithRouterProps<Params>,
};

type Props = {
  ...StateProps,
  ...DispatchProps,
  ...InjectedProps,
};

const cleanEmptyValue = (value?: string): void | string =>
  value === '' ? undefined : value;

class Presenter extends React.Component<Props> {
  static preFetch({
    dispatch,
    getState,
    params,
  }: PreFetchArgs<void, Params, empty, FullState>): PreFetchReturn<mixed> {
    const currentId = selectCurrentId(getState());

    const orgId = ['myself', 'organization'].includes(params.orgId)
      ? currentId
      : params.orgId;

    const org =
      currentId === orgId
        ? selectShopCurrent(getState(), false)
        : selectOneOrganization(getState(), orgId);

    return AddressField.preFetch({
      dispatch,
      country: org?.country,
    });
  }

  destroyOnUnmount: boolean = true;

  componentDidMount() {
    if (!this.props.fetchingOrg && !this.props.loadedOrg) {
      this.props.fetchOneOrganization(this.props.orgId);
    }

    if (!this.props.fetchingInfo && !this.props.loadedInfo) {
      this.props.fetchInfo({ sharingId: this.props.orgId });
    }
  }

  componentWillUnmount() {
    if (this.destroyOnUnmount) {
      this.props.destroyForm();
    }
  }

  isFirstBuy(): boolean {
    return 'first' in this.props.location.query;
  }

  filterOrganizationDataToPatch(
    fields: Array<string>,
    initial: Organization,
    data: Partial<Organization>,
  ): Partial<Organization> {
    return fields.reduce((acc, current) => {
      if (
        Object.keys(data).indexOf(current) === -1 ||
        // $FlowFixMe[invalid-computed-prop]
        (data[current] && initial[current] === data[current])
      ) {
        return acc;
      }

      // $FlowIgnore[prop-missing]
      // $FlowFixMe[invalid-computed-prop]
      acc[current] = cleanEmptyValue(data[current]);
      return acc;
    }, {});
  }

  handleSubmit: Organization => Promise<void> | Promise<void | any> = data => {
    const {
      params: { cartId },
      location: {
        query: { owner = 'cart' },
      },
      org,
    } = this.props;

    if (!org) {
      return Promise.resolve();
    }

    // common fields
    let fields = [
      'name',
      'streetaddr',
      'streetaddr2',
      'zip',
      'city',
      'country',
      'state',
      'email',
      'phone',
      'fax',
    ];

    // if not personal orga or no lastname
    if (!this.props.myself || !org.lastname) {
      fields = ['lastname'].concat(fields);
    }

    // if not personal orga or no firstname
    if (!this.props.myself || !org.firstname) {
      fields = ['firstname'].concat(fields);
    }

    // if not personal orga
    if (!this.props.myself) {
      fields = fields.concat(['siren', 'vat_number']);
    }

    // $FlowFixMe[incompatible-call]
    const patchData = this.filterOrganizationDataToPatch(fields, org, data);

    // Always send address information if country change
    if (patchData.country) {
      patchData.streetaddr = cleanEmptyValue(data.streetaddr);
      patchData.streetaddr2 = cleanEmptyValue(data.streetaddr2);
      patchData.city = cleanEmptyValue(data.city);
      patchData.state = cleanEmptyValue(data.state);
      patchData.zip = cleanEmptyValue(data.zip);
    }

    const currency =
      // $FlowFixMe[prop-missing] - currency is not in Organization
      this.props.currency !== data.currency ? data.currency : null;

    const promises = [];
    const confirm = this.props.myself && !org.country;
    const to = confirm
      ? `/cart/${cartId}/configure/account-choice/confirm/organization/${org.id}?owner=${owner}`
      : `/cart/${cartId}/configure/account-choice`;

    this.props.setPatchContactsFlag(true);
    if (confirm) {
      this.destroyOnUnmount = false;
    }

    if (Object.keys(patchData).length || currency) {
      promises.push(
        this.props.patchOrganizationAndPrepaid(
          this.props.orgId,
          patchData,
          // $FlowFixMe[incompatible-call]
          currency,
          confirm,
        ),
      );
    }

    return Promise.allSettled(promises)
      .then(results => {
        const rejectedPromises = results.filter(pr => pr.status === 'rejected');

        if (rejectedPromises.length) {
          const allErrors = rejectedPromises.reduce(
            (acc, val) => ({
              // $FlowFixMe - if there's a reason for rejection
              errors: { ...acc.errors, ...val?.reason?.errors },
            }),
            { errors: null },
          );
          throw allErrors;
        }

        // Redirect the user to confirmation screen only if they just completed
        // their personal organization for the first time.
        this.props.navigate(to);
      })
      .catch(err => {
        if (confirm) {
          this.destroyOnUnmount = true;
        }
        if (err.errors) {
          throw new SubmissionError(err.errors);
        } else if (err.originalError) {
          throw new SubmissionError(err.originalError.errors);
        } else if (fields.some(f => f in err)) {
          throw new SubmissionError(err);
        } else {
          throw new SubmissionError({
            _error: this.props.translator.__(
              'Unable to save account information. Please try again later.',
            ),
          });
        }
      });
  };

  render(): null | React.Node {
    if (!this.props.org) {
      return null;
    }

    const { id, ...initialValues } = {
      ...this.props.org,
      // `name` is uniq and should not be updated nor passed to create process as it could be at first buy
      name: this.isFirstBuy() ? undefined : this.props.org?.name,
      currency: this.props.currency,
    };

    const canChangeCurrency = !this.props.currency || !this.props.prepaidAmount;

    const dryrun = this.props.myself && !this.props.org?.country;

    return (
      <AccountForm
        bu={this.props.bu}
        cartId={this.props.params.cartId}
        hasOwnerOnly={this.props.hasOwnerOnly}
        canChangeCurrency={canChangeCurrency}
        currencies={this.props.currencies}
        initialValues={initialValues}
        isBuyingForMyself={this.props.myself}
        onSubmit={this.handleSubmit}
        destroyOnUnmount={!dryrun}
        forceUnregisterOnUnmount={dryrun}
        onTypeChange={
          this.isFirstBuy()
            ? () => {
                this.destroyOnUnmount = false;
              }
            : undefined
        }
      />
    );
  }
}

export default Presenter;
